/* Карточка клиента: показатели, авто, согласия (каждое изменение — в журнал на сервере), заметки, история визитов. */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { STATUS, dateTime, money, phoneFmt, type Consents, type Customer, type Vehicle } from "@okno/shared";
import { api } from "../../lib/api";
import { useActions } from "../../lib/actions";
import { locKey, useSession } from "../../lib/session";
import type { CustomerDetails } from "../../lib/types";
import { Icon } from "../../ui/Icon";
import { Empty, Spinner } from "../../ui/kit";
import { Drawer } from "../../ui/layers";
import { useToast } from "../../ui/toast";
import { ClientHistory } from "./ClientHistory";
import { ClientVehicles } from "./ClientVehicles";
import { CHANNELS, ClientTags, Mini, type ConsentKey } from "./clientParts";

interface PatchResult {
  customer: Customer;
  vehicles: Vehicle[];
}

export function ClientDrawer({ customerId }: { customerId: string }) {
  const { locationId } = useSession();
  const key = locKey(locationId, "customer", customerId);
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<CustomerDetails>("/locations/" + locationId + "/customers/" + customerId),
  });

  if (q.isPending) {
    return (
      <Drawer title="Клиент">
        <Spinner />
      </Drawer>
    );
  }
  if (q.isError || !q.data) {
    return (
      <Drawer title="Клиент">
        <Empty icon="alert" title="Не удалось открыть карточку" text="Клиент не найден или нет доступа." />
      </Drawer>
    );
  }
  return <ClientCard data={q.data} queryKey={key} />;
}

function ClientCard({ data, queryKey }: { data: CustomerDetails; queryKey: unknown[] }) {
  const { locationId, can } = useSession();
  const { openOrder, openBooking } = useActions();
  const qc = useQueryClient();
  const toast = useToast();
  const { customer: c, vehicles, stats: s, orders } = data;
  const canEdit = can("booking");
  const base = "/locations/" + locationId + "/customers/" + c.id;

  // ответ PATCH сразу кладём в кэш карточки, остальное (список) обновит SSE
  const applyPatch = (r: PatchResult) => qc.setQueryData<CustomerDetails>(queryKey, (prev) => (prev ? { ...prev, customer: r.customer, vehicles: r.vehicles } : prev));

  const [pending, setPending] = useState<Partial<Consents>>({});
  const setConsent = async (k: ConsentKey, v: boolean) => {
    const label = CHANNELS.find((x) => x[0] === k)?.[1] || k;
    setPending((p) => ({ ...p, [k]: v }));
    try {
      applyPatch(await api.patch<PatchResult>(base, { consents: { [k]: v } }));
      toast(label + ": " + (v ? "согласие есть" : "согласие отозвано"));
    } catch (e) {
      toast.error(e);
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[k];
        return next;
      });
    }
  };

  const [notes, setNotes] = useState(c.notes || "");
  const saveNotes = async () => {
    const v = notes.trim();
    if (v === (c.notes || "").trim()) return;
    try {
      applyPatch(await api.patch<PatchResult>(base, { notes: v }));
      toast("Заметка сохранена");
    } catch (e) {
      toast.error(e);
    }
  };

  const foot = (
    <>
      <a className="btn" href={"tel:+" + c.phone}>
        <Icon name="phone" size={16} />
        Позвонить
      </a>
      <span style={{ flex: 1 }} />
      {canEdit ? (
        <button className="btn btn-primary" onClick={() => openBooking({ customerId: c.id, vehicleId: vehicles[0]?.id })}>
          <Icon name="plus" size={16} />
          Записать
        </button>
      ) : null}
    </>
  );

  const sub = (
    <>
      <a href={"tel:+" + c.phone}>{phoneFmt(c.phone)}</a> · источник: {c.source}
    </>
  );

  return (
    <Drawer title={c.name} sub={sub} foot={foot} label={c.name}>
      <div className="grid grid-2" style={{ gap: 8 }}>
        <Mini label="Визитов" value={s.visits} />
        <Mini label="Средний чек" value={s.visits ? money(s.avg) : "—"} />
        <Mini label="Последний визит" value={s.last ? s.daysSince + " дн. назад" : "—"} />
        <Mini label="Обычно" value={"раз в " + c.interval + " дн."} />
      </div>
      <ClientTags customer={c} stats={s} />
      {s.upcoming ? <UpcomingCallout upcoming={s.upcoming} orders={data.orders} onOpen={openOrder} /> : null}

      <ClientVehicles customerId={c.id} vehicles={vehicles} canEdit={canEdit} queryKey={queryKey} />

      <section>
        <div className="section-title">Согласия на коммуникации</div>
        <div className="panel panel-body stack stack-sm">
          {CHANNELS.map(([k, l]) => (
            <label key={k} className="switch row-between" style={{ display: "flex" }}>
              <span>
                {l}
                {k === "geo" ? (
                  <span className="cell-sub" style={{ display: "block" }}>
                    Для предложений рядом с точкой
                  </span>
                ) : null}
              </span>
              <span style={{ display: "flex" }}>
                <input type="checkbox" checked={pending[k] ?? c.consents[k]} disabled={!canEdit || pending[k] !== undefined} onChange={(e) => void setConsent(k, e.target.checked)} />
                <span className="track" />
              </span>
            </label>
          ))}
          <p className="cell-sub">Без согласия клиент не попадёт в рассылку. Каждое изменение записывается в журнал.</p>
        </div>
      </section>

      <label className="field">
        <span>Заметки</span>
        <textarea className="textarea" placeholder="Предпочтения, особенности авто" value={notes} disabled={!canEdit} maxLength={2000} onChange={(e) => setNotes(e.target.value)} onBlur={() => void saveNotes()} />
      </label>

      <ClientHistory orders={orders} onOpen={openOrder} />
    </Drawer>
  );
}

function UpcomingCallout({ upcoming, orders, onOpen }: { upcoming: NonNullable<CustomerDetails["stats"]["upcoming"]>; orders: CustomerDetails["orders"]; onOpen: (id: string) => void }) {
  const o = orders.find((x) => x.id === upcoming.id);
  return (
    <button className="callout" onClick={() => onOpen(upcoming.id)} style={{ border: 0, textAlign: "left", cursor: "pointer" }}>
      <Icon name="calendar" size={16} />
      <span>
        Запись: {dateTime(upcoming.start)}
        {o ? " · " + STATUS[o.status].label : ""}
      </span>
    </button>
  );
}
